import React, { useState } from 'react';
import { ModalsKeys } from 'global';
import { useSelector } from 'react-redux';
import { useModal } from 'hooks/useModal';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { useHistory, useLocation } from 'react-router-dom';
import { CreateProposalProductDialog } from 'modules/proposals';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { SubmitProposalOptionActionType } from '@trnsact/trnsact-shared-types';
import { QUERY_ACTIVE_OCA_TEMPLATE } from 'modules/vendorOpportunity/insurance/api';
import { VOApi } from 'modules/vendorOpportunity/api';
import { FormSections, DeskingSubmitOptions, SaleType } from 'modules/desking/types';
import { CreateLeadProposalResponse, CreateLeadProposalVariables } from 'modules/startDeal/api/types';
import {
  DeskingPanel,
  deskingConfigByAccId,
  ProductsDynamicFields,
  collectVariablesForCreateProposal,
} from 'modules/desking';
import {
  LeadSection,
  ApiContainer,
  EquipmentSection,
  collectEquipmentDTO,
  ProposalCreationModal,
  generateCreateLeadProposalLeadDTO,
} from 'modules/startDeal';
import { useAftermarketProducts } from 'modules/aftermarket';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { StartDealPageQueryParams } from 'modules/startDeal/types';
import {
  PreviewExistingImagesDialog,
  PreviewNewEquipmentImagesDialog,
  UploadImagesForNewEquipmentDialog,
} from 'modules/equipment';
import { SyncDealInfoOnStartDeal } from 'modules/equipment/ui/syncDealInfo/SyncDealInfoOnStartDeal';
import { SearchInventoryDialog } from 'modules/inventory';

const modals = {
  [ModalsKeys.SearchInventoryDialog]: <SearchInventoryDialog />,
  [ModalsKeys.ProposalCreationDialog]: <ProposalCreationModal />,
  [ModalsKeys.ProposalCreateProductDialog]: <CreateProposalProductDialog />,
  [ModalsKeys.UploadImagesForNewEquipmentDialog]: <UploadImagesForNewEquipmentDialog />,
  [ModalsKeys.PreviewNewEquipmentImagesDialog]: <PreviewNewEquipmentImagesDialog />,
  [ModalsKeys.PreviewExistingEquipmentImagesDialog]: <PreviewExistingImagesDialog />,
};

export const StartDealPage = () => {
  const { push } = useHistory();
  const location = useLocation<StartDealPageQueryParams>();
  const queryParams = new URLSearchParams(location.search);

  const { showNotification } = useNotifications();
  const { handleAftermarketProductCreate } = useAftermarketProducts();
  const { handleOpen: handleOpenLoadingModal, handleClose: handleCloseLoadingModal } = useModal(
    ModalsKeys.ProposalCreationDialog
  );

  const account = useSelector((state: any) => state.account);
  const vendorProfile = useSelector((state: any) => state.vp);
  const userProfile = useSelector((state: any) => state.userProfile);
  const aftermarketPortalConfigurations = useSelector(portalConfigurationsSelectors.getAftermarket);

  const doesUserHaveAccessToDealSync = useSelector(portalConfigurationsSelectors.checkAccessInventorySearch);

  const [externalDealIdForSync, setExternalDealIdForSync] = useState<string>('');
  const [externalDealInfo, setExternalDealInfo] = useState<any>(null);

  const { data } = useQuery(QUERY_ACTIVE_OCA_TEMPLATE, {
    skip: !vendorProfile?.id,
    variables: { vendorProfileId: vendorProfile?.id, isActive: true },
  });

  const [getDealInfoFromExternalDms, { loading: isGettingDealInfoFromFromExternalDms }] = useLazyQuery(
    VOApi.getDealInfoFromExternalDms,
    {
      variables: { input: { externalDealId: externalDealIdForSync } },
      onCompleted: data => {
        setExternalDealInfo(data.getDealInfoFromExternalDms);
        showNotification('Deal information successfully retrieved!');
      },
      onError() {
        showNotification('Error', { type: 'error' });
      },
    }
  );

  function stripTypeNames<T>(value: T): T {
    if (Array.isArray(value)) {
      return value.map(v => stripTypeNames(v)) as unknown as T;
    }
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (k === '__typename') continue;
        out[k] = stripTypeNames(v as any);
      }
      return out as T;
    }
    return value;
  }

  const handleSubmitDesking = async (
    options: DeskingSubmitOptions,
    onCreateLead: (input: CreateLeadProposalVariables['input']) => Promise<CreateLeadProposalResponse['leadProposal']>
  ) => {
    const { form, builder, commonProductsConfiguration, productsConfigInMenu, isDraft, pricing } = options;

    if (!form.contact.email || !form.contact.fullName) {
      showNotification('Lead Email and Contact required', { type: 'error' });
      return;
    }

    if (!form.financeQuote.terms.length && form.saleDetails.saleType !== SaleType.Cash) {
      showNotification('Terms required', { type: 'error' });
      return;
    }

    const proposalSubmitType = form.displaySettings.shouldRedirectToOCA
      ? SubmitProposalOptionActionType.OcaRedirect
      : SubmitProposalOptionActionType.AcceptOption;

    handleOpenLoadingModal();

    const {
      input: { proposalMenuId, financeProgramId, saleType, settings, paymentOptions, sendProposalData },
    } = collectVariablesForCreateProposal({
      isDraft,
      pricing,
      voId: '',
      formData: form,
      menuByTerms: builder,
      productsConfigInMenu,
      commonProductsConfiguration,
      submitType: proposalSubmitType,
      vendorContactId: queryParams.get('vendorContactId'),
    });

    const isDealerAssignIdAndLocationExist = form.contact.assignDealer?.value && form.contact.dealerLocation;

    const dealInfoFromExternalDms = stripTypeNames({
      ...externalDealInfo,
      externalDmsId: `ARCADIUM:${externalDealIdForSync}`, // TODO: Update 'ARCADIUM' to be dynamic once needed
    });

    const { success, id, vendorGUID, vendorOpportunityId } = await onCreateLead({
      equipmentListDTO: collectEquipmentDTO(form.equipment),
      equipmentDTO: null, //@todo: REMOVE THIS LINE WHEN EQUIPMENT IS NOT MAPPED ON THE GRAPHQL API ANYMORE
      leadDTO: generateCreateLeadProposalLeadDTO(form.contact, form.saleDetails.saleType),
      proposalDTO: { financeProgramId, paymentOptions, proposalMenuId, saleType, settings },
      dealer: isDealerAssignIdAndLocationExist
        ? { assignId: form.contact.assignDealer?.value ?? null, locationId: form.contact.dealerLocation }
        : null,
      sendProposalData: sendProposalData,
      dealInfoFromExternalDms,
    });

    handleCloseLoadingModal();

    if (success) {
      showNotification('Proposal created!');
      push(`/vo/${vendorOpportunityId}`);

      const linkToProposalPage = generateProposalPageURL(id!, vendorGUID!);
      window.open(linkToProposalPage, '_blank');
    } else {
      showNotification('Something went wrong', { type: 'error' });
    }
  };

  const handleFillInDealInfo = async (externalDealId: string) => {
    await getDealInfoFromExternalDms();
    console.log('externalDmsData');
  };

  const onExternalDealIdChange = (externalDealId: string) => {
    setExternalDealIdForSync(externalDealId);
  };

  return (
    <PageContainer modals={modals}>
      <CardContainer>
        <RenderWithCondition condition={Boolean(account.id)}>
          {doesUserHaveAccessToDealSync && (
            <SyncDealInfoOnStartDeal
              externalDealId={externalDealIdForSync}
              doesUserHaveAccessToDealSync={doesUserHaveAccessToDealSync}
              handleFillInDealInfo={handleFillInDealInfo}
              onExternalDealIdChange={onExternalDealIdChange}
              isGettingDealInfoFromFromExternalDms={isGettingDealInfoFromFromExternalDms}
            />
          )}
          <ApiContainer account={account}>
            {startDealRenderProps => (
              <DeskingPanel
                accountId={account.id}
                isSupportSaleDetailsUpdating
                userEmail={userProfile.email}
                onCreateProposalProduct={handleAftermarketProductCreate}
                configurationByAccount={deskingConfigByAccId[account.id]}
                isProposalLoading={startDealRenderProps.isProposalLoading}
                isSupportBehaviorSetting={!!(data?.getOcaTemplatesByVendorProfile ?? []).length}
                isProductCreationAvailable={aftermarketPortalConfigurations?.allowCreateCustomProducts}
                initialLenderProfileId={queryParams.get('lenderProfileId') ?? null}
                initialFinanceProgramId={queryParams.get('financeProgramId') ?? null}
                allAvailableCcEmailOptions={[userProfile]}
                formFieldsConfiguration={{
                  'submission.maxApprovalAmount': false,
                  'submission.selectedSubmissionId': false,
                }}
                externalDealInfo={externalDealInfo}
                customSections={{
                  [FormSections.Contact]: () => (
                    <LeadSection
                      sectionName={FormSections.Contact}
                      {...startDealRenderProps}
                      externalDealInfo={externalDealInfo}
                    />
                  ),
                  [FormSections.Equipment]: (onChangeProductsConfiguration, configurationByAccount) => (
                    <EquipmentSection
                      sectionName={FormSections.Equipment}
                      formFieldsRequire={configurationByAccount?.formFieldsRequire ?? null}
                      dynamicFieldsSlot={
                        <ProductsDynamicFields onChangeAllProductsConfig={onChangeProductsConfiguration} />
                      }
                      {...startDealRenderProps}
                      onChangeAllProductsConfig={onChangeProductsConfiguration}
                      externalDealInfo={externalDealInfo}
                    />
                  ),
                }}
                onSubmitDesking={async options => {
                  await handleSubmitDesking(options, startDealRenderProps.handleCreateLeadProposal);
                }}
              />
            )}
          </ApiContainer>
        </RenderWithCondition>
      </CardContainer>
    </PageContainer>
  );
};
